"use client";

import { useEffect, useRef, useState } from "react";
import allArtistComments from "../data/allArtistComments.json";
import Comment from "./comment";
import "./comment.css";
import DateSelector from "./date-select";
import DateSelectList from "./date-select-list";
import CommentSearch from "./comment-search";

const dayList = Object.keys(allArtistComments);

const albumName = {
  20240705: "songbird",
  20241002: "steady",
  20250122: "miracle",
  20250414: "poppop",
  20250416: "poppop",
  20250417: "poppop",
  20250418: "poppop",
  20250419: "poppop",
  20250420: "poppop",
  20250812: "surf",
  20250813: "surf",
  20250814: "surf",
  20250816: "surf",
  20250901: "color",
  20250902: "color",
  20250903: "color",
  20250905: "color",
  20250906: "color",
  20250907: "color",
  20250908: "color",
  20250915: "color",
  20251104: "color",
  20260420: "odetolove",
  20260421: "odetolove",
  20260422: "odetolove",
  20260423: "odetolove",
  20260424: "odetolove",
  20260425: "odetolove",
  20261012: "ispy",
  20261013: "ispy",
  20261014: "ispy",
  20261015: "ispy",
  20261016: "ispy",
  20261017: "ispy",
  20261018: "ispy",
  20261019: "ispy",
  20261020: "ispy",
  20261021: "ispy",
};

export default function Handler() {
  const [selectedDay, setSelectedDay] = useState(dayList[dayList.length - 1]);
  const [comments, setComments] = useState([]);
  const [imageOpen, setImageOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showTime, setShowTime] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // 검색 결과에서 클릭한 댓글로 스크롤해야 할 때, 그 댓글의 COMMENTSEQ를 잠깐 담아두는 ref.
  // (state로 만들면 값이 바뀔 때마다 불필요한 리렌더가 생겨서 ref로 처리)
  const pendingScrollSeqRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize(); // 처음 실행
    window.addEventListener("resize", handleResize);
    setIsLoading(false);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const closeModal = () => {
    setImageOpen(false);
  };

  const openModal = (image) => {
    setModalImage(image);
    setImageOpen(true);
  };

  useEffect(() => {
    try {
      let selectedDateAllComments = [];
      allArtistComments[selectedDay].forEach((item) => {
        const parsed = JSON.parse(item.ARTISTCOMMENT);
        if (item.ATTACHIMAGES.length > 0) {
          selectedDateAllComments.push({
            ...parsed,
            IMAGEURL: item.ATTACHIMAGES,
            REGDATE: item.REGDATE,
            COMMENTSEQ: item.COMMENTSEQ,
          });
        } else {
          selectedDateAllComments.push({
            ...parsed,
            REGDATE: item.REGDATE,
            COMMENTSEQ: item.COMMENTSEQ,
          });
        }
      });
      setComments(selectedDateAllComments);
      // 스크롤 처리는 아래 comments 변화를 감지하는 useEffect에서 담당한다.
    } catch (e) {}
  }, [selectedDay]);

  const scrollRef = useRef(null);

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
      });
    }
  };

  // 댓글 목록(comments)이 실제로 다시 그려진 뒤 실행된다.
  // pendingScrollSeqRef에 목표가 있으면 그 댓글로 스크롤 + 하이라이트,
  // 없으면 기존처럼 맨 위로 스크롤한다.
  useEffect(() => {
    if (comments.length === 0) return;

    const targetSeq = pendingScrollSeqRef.current;

    if (targetSeq == null) {
      scrollToTop();
      return;
    }

    pendingScrollSeqRef.current = null;

    // DOM이 실제로 그려질 시간을 한 프레임 준다.
    requestAnimationFrame(() => {
      const el = document.getElementById(`comment-${targetSeq}`);
      if (!el) {
        scrollToTop();
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });

      // 잠깐 배경색을 밝혔다가 원래대로 — CSS 파일 수정 없이 인라인으로 처리
      const prevTransition = el.style.transition;
      const prevBackground = el.style.backgroundColor;
      el.style.transition = "background-color 0.4s ease";
      el.style.backgroundColor = "rgba(242, 199, 68, 0.18)";
      setTimeout(() => {
        el.style.backgroundColor = prevBackground;
        setTimeout(() => {
          el.style.transition = prevTransition;
        }, 400);
      }, 2000);
    });
  }, [comments]);

  if (isLoading) {
    return;
  }

  const toggleShowTime = () => setShowTime((prev) => !prev);

  // 검색 결과 클릭 시 호출됨: 해당 날짜로 이동하고, 다음 렌더 후 그 댓글로 스크롤한다.
  const navigateToComment = (date, commentSeq) => {
    pendingScrollSeqRef.current = commentSeq;
    setIsSearchOpen(false);
    if (date === selectedDay) {
      // 이미 같은 날짜를 보고 있다면 comments state 변화가 없으므로 직접 스크롤 트리거
      requestAnimationFrame(() => {
        const el = document.getElementById(`comment-${commentSeq}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        pendingScrollSeqRef.current = null;
      });
    } else {
      setSelectedDay(date);
    }
  };

  return (
    <>
      <div className="root-component">
        {!isMobile ? (
          <DateSelectList
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
          />
        ) : null}
        <div
          ref={scrollRef}
          className={`main ${albumName[selectedDay]}`}
          style={{}}
        >
          {isMobile ? (
            <DateSelector
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              toggleShowTime={toggleShowTime}
              setIsSearchOpen={setIsSearchOpen}
            />
          ) : null}
          {comments && (
            <div style={{}}>
              <div className="comment-date">{`${selectedDay.slice(
                0,
                4
              )}.${selectedDay.slice(4, 6)}.${selectedDay.slice(6, 8)}`}</div>

              <div className="comment-list">
                {comments.map((item, index) => (
                  <div id={`comment-${item.COMMENTSEQ}`} key={index}>
                    <Comment>
                      <Comment.ProfileImage src={item.imgUrl} />
                      {item.IMAGEURL ? (
                        <Comment.Content
                          name={item.artistName}
                          text={item.comment}
                          time={item.REGDATE}
                          contentImage={item.IMAGEURL}
                          openModal={openModal}
                          showTime={showTime}
                        />
                      ) : (
                        <Comment.Content
                          name={item.artistName}
                          text={item.comment}
                          time={item.REGDATE}
                          showTime={showTime}
                        />
                      )}
                    </Comment>
                  </div>
                ))}
              </div>
            </div>
          )}
          {imageOpen && (
            <div className="modal-overlay" onClick={closeModal}>
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={modalImage}
                  alt="원본 이미지"
                  className="modal-image"
                />
                <button className="modal-close-button" onClick={closeModal}>
                  ×
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CommentSearch
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelectComment={navigateToComment}
      />
    </>
  );
}
